import styled from '@emotion/styled'

import { maxWidths } from '../../theme/index'

export type ThemeMaxWidths = typeof maxWidths

/**
 * `Center` Component
 *
 * @overview
 * The `Center` component is designed to center its children and optionally
 * clamp its width at a predefined value. By default, this value is set to the
 * max page width (1680px). Additionally, it offers options to center text alignment
 * and children alignment.
 *
 * @example
 *
 * ```javascript
 * <Center maxWidth="content" centerText={true}>
 *   Centered content with a max width based on content.
 * </Center>
 * ```
 */
const Center = styled.div<{
  /**
   * Sets the max-inline size of the component.
   * It can be a CSSLength, a number, or a key from the theme's maxWidths.
   * Default is set to 'page' from maxWidths.
   */
  maxWidth?: keyof ThemeMaxWidths | string | number
  /**
   * Sets the text alignment of the component to center.
   */
  centerText?: boolean
  /**
   * Sets the child element alignment to center. (justify-content)
   */
  centerChildren?: boolean
}>`
  max-width: ${props => {
    if (typeof props.maxWidth === 'number') {
      return `${props.maxWidth}px`
    }
    return maxWidths[(props.maxWidth as keyof ThemeMaxWidths) || 'page'] || props.maxWidth
  }};
  text-align: ${props => (props.centerText ? 'center' : 'inherit')};
  display: flex;
  flex-direction: column;
  justify-content: ${props => (props.centerChildren ? 'center' : 'flex-start')};
  align-items: center;
  width: 100%;
`

export default Center
